import React, { useCallback, useEffect, useMemo } from 'react'

import {
    NaturalFlowSegments,
    type NaturalFlowGeometryStore,
} from 'components/panels/NaturalFeedSegments'
import { isHydratedAnswerCacheEntry } from 'state/cache'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import useGetAnswer from 'refactor/hooks/api/useGetAnswer'
import { buildNaturalFlowSegments, type NaturalFlowSegment } from 'lib/naturalFeedSegments'
import { readThreadSegmentGeometry, rememberThreadSegmentGeometry } from 'lib/threadLayoutMemory'
import { type ThreadPostDescriptor, updateThreadSessionGeometry } from 'lib/threadSession'
import styles from './thread-natural-segments.module.scss'

export const THREAD_NATURAL_FLOW_LAYOUT_VERSION = 1
export const THREAD_NATURAL_FLOW_SEGMENT_SIZE = 2
export const THREAD_NATURAL_FLOW_INITIAL_MOUNT_SEGMENT_COUNT = 4
// Keep data warm far ahead of the viewport, without paying the DOM, layout, subscription,
// and animation cost until a segment enters the much smaller render window.
export const THREAD_NATURAL_FLOW_PREFETCH_ROOT_MARGIN = '22000px 0px 22000px 0px'
export const THREAD_NATURAL_FLOW_FORWARD_PREFETCH_ROOT_MARGIN = '3000px 0px 22000px 0px'
export const THREAD_NATURAL_FLOW_BACKWARD_PREFETCH_ROOT_MARGIN = '22000px 0px 3000px 0px'
export const THREAD_NATURAL_FLOW_RETENTION_ROOT_MARGIN = '7000px 0px 7000px 0px'
export const THREAD_NATURAL_FLOW_URGENT_ROOT_MARGIN = '3000px 0px 3000px 0px'
export const THREAD_NATURAL_FLOW_FORWARD_DOM_ROOT_MARGIN = '3000px 0px 7000px 0px'
export const THREAD_NATURAL_FLOW_BACKWARD_DOM_ROOT_MARGIN = '7000px 0px 3000px 0px'

type ThreadHydratedAnswerRowProps = {
    descriptor: ThreadPostDescriptor
    estimatedHeight: number
    index: number
    renderAnswer: (answer: any, index: number) => React.ReactNode
    threadKey: string
}

function ThreadHydratedAnswerRow({
    descriptor,
    estimatedHeight,
    index,
    renderAnswer,
    threadKey,
}: ThreadHydratedAnswerRowProps) {
    const { answer, isError, isFetching } = useGetAnswer(descriptor.answerId)
    useEffect(() => {
        if (isHydratedAnswerCacheEntry(answer)) {
            updateThreadSessionGeometry(threadKey, answer)
        }
    }, [answer, threadKey])

    if (!isHydratedAnswerCacheEntry(answer)) {
        const unavailable = isError && !isFetching
        return (
            <div
                aria-busy={unavailable ? undefined : true}
                aria-label={unavailable ? 'Post unavailable' : 'Loading post'}
                className={styles.threadAnswerHydrating}
                data-answer-row-id={descriptor.answerId}
                data-thread-answer-hydrating={unavailable ? undefined : 'true'}
                data-thread-answer-unavailable={unavailable ? 'true' : undefined}
                data-thread-answer-placeholder-visible="true"
                data-natural-flow-geometry-pending={unavailable ? undefined : 'true'}
                role="article"
                style={{ minHeight: Math.max(1, estimatedHeight) }}
            >
                {unavailable ? (
                    <span className={styles.unavailableText}>Post unavailable</span>
                ) : (
                    <>
                        <div className={styles.skeletonHeader} aria-hidden="true">
                            <span className={styles.skeletonAvatar} />
                            <span className={styles.skeletonIdentity}>
                                <span className={styles.skeletonLineWide} />
                                <span className={styles.skeletonLineNarrow} />
                            </span>
                        </div>
                        <div className={styles.skeletonBody} aria-hidden="true">
                            <span className={styles.skeletonLineWide} />
                            <span className={styles.skeletonLineMedium} />
                        </div>
                    </>
                )}
            </div>
        )
    }

    return <>{renderAnswer(answer, index)}</>
}

type ThreadNaturalSegmentsProps = {
    descriptors: ThreadPostDescriptor[]
    forceMountAll: boolean
    getEstimatedAnswerHeight: (descriptor: ThreadPostDescriptor) => number
    getRowLayoutSignature: (descriptor: ThreadPostDescriptor) => string
    pinnedAnswerIds: Set<string>
    renderAnswer: (answer: any, index: number) => React.ReactNode
    threadKey: string
}

const threadGeometryStore: NaturalFlowGeometryStore = {
    read: readThreadSegmentGeometry,
    remember: rememberThreadSegmentGeometry,
}

export default function ThreadNaturalSegments({
    descriptors,
    forceMountAll,
    getEstimatedAnswerHeight,
    getRowLayoutSignature,
    pinnedAnswerIds,
    renderAnswer,
    threadKey,
}: ThreadNaturalSegmentsProps) {
    const segments = useMemo(
        () =>
            buildNaturalFlowSegments(descriptors, descriptor => descriptor.rowKey, {
                keyPrefix: `natural-thread:v${THREAD_NATURAL_FLOW_LAYOUT_VERSION}`,
                segmentSize: THREAD_NATURAL_FLOW_SEGMENT_SIZE,
            }),
        [descriptors],
    )
    const getEstimatedHeight = useCallback(
        (segment: NaturalFlowSegment<ThreadPostDescriptor>) =>
            segment.rows.reduce(
                (height, descriptor) => height + getEstimatedAnswerHeight(descriptor),
                0,
            ),
        [getEstimatedAnswerHeight],
    )
    const getLayoutSignature = useCallback(
        (segment: NaturalFlowSegment<ThreadPostDescriptor>) =>
            [
                `thread-layout:v${THREAD_NATURAL_FLOW_LAYOUT_VERSION}`,
                ...segment.rows.map(getRowLayoutSignature),
            ].join('|'),
        [getRowLayoutSignature],
    )
    const isAlwaysMounted = useCallback(
        (segment: NaturalFlowSegment<ThreadPostDescriptor>) =>
            forceMountAll ||
            segment.rows.some(descriptor => pinnedAnswerIds.has(descriptor.answerId)),
        [forceMountAll, pinnedAnswerIds],
    )
    const renderRow = useCallback(
        (descriptor: ThreadPostDescriptor, tailIndex: number) => (
            <ThreadHydratedAnswerRow
                key={descriptor.rowKey}
                descriptor={descriptor}
                estimatedHeight={getEstimatedAnswerHeight(descriptor)}
                // The title remains outside the segmented tail at index zero.
                index={tailIndex + 1}
                renderAnswer={renderAnswer}
                threadKey={threadKey}
            />
        ),
        [getEstimatedAnswerHeight, renderAnswer, threadKey],
    )
    const prefetchSegment = useCallback((segment: NaturalFlowSegment<ThreadPostDescriptor>) => {
        queueAnswerHydration(
            segment.rows.map(descriptor => descriptor.answerId),
            { lane: 'visible' },
        )
    }, [])

    return (
        <div data-thread-natural-flow="true">
            <NaturalFlowSegments
                backwardPrefetchRootMargin={THREAD_NATURAL_FLOW_BACKWARD_PREFETCH_ROOT_MARGIN}
                backwardRootMargin={THREAD_NATURAL_FLOW_BACKWARD_DOM_ROOT_MARGIN}
                forwardPrefetchRootMargin={THREAD_NATURAL_FLOW_FORWARD_PREFETCH_ROOT_MARGIN}
                forwardRootMargin={THREAD_NATURAL_FLOW_FORWARD_DOM_ROOT_MARGIN}
                geometryStore={threadGeometryStore}
                getEstimatedHeight={getEstimatedHeight}
                getLayoutSignature={getLayoutSignature}
                initialMountSegmentCount={THREAD_NATURAL_FLOW_INITIAL_MOUNT_SEGMENT_COUNT}
                isAlwaysMounted={isAlwaysMounted}
                onPrefetchSegment={prefetchSegment}
                prefetchRootMargin={THREAD_NATURAL_FLOW_PREFETCH_ROOT_MARGIN}
                renderRow={renderRow}
                retentionRootMargin={THREAD_NATURAL_FLOW_RETENTION_ROOT_MARGIN}
                segments={segments}
                sessionKey={threadKey}
                surface="thread"
                urgentRootMargin={THREAD_NATURAL_FLOW_URGENT_ROOT_MARGIN}
            />
        </div>
    )
}
